import React, { useCallback, useState, createContext, useContext, useEffect, useLayoutEffect } from 'react';
import {
  ReactFlow,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  MarkerType,
  BaseEdge,
  EdgeLabelRenderer,
  getBezierPath,
  getSmoothStepPath,
  Handle,
  Position,
  useReactFlow,
  ReactFlowProvider,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { Box, ToggleButton, ToggleButtonGroup, Typography, Chip } from '@mui/material';
import {
  AccountTree as TreeIcon,
  SwapVert as VerticalIcon,
  SwapHoriz as HorizontalIcon,
} from '@mui/icons-material';
import { formatShareholdingPercent } from '../../utils/shareholdingPercent';
import { getShareholderTypeLabel, getSideShareholderChartTheme } from '../../utils/shareholderTypes';

// Context for chart state
export const HierarchyChartContext = createContext({
  selectedNode: null,
  setSelectedNode: () => {},
  onToggle: () => {},
  edgePathType: 'step',
  layoutDirection: 'TB',
});

// Constants
const NODE_WIDTH = 220;
const NODE_HEIGHT = 120;
const EXTERNAL_NODE_WIDTH = 190;
const EXTERNAL_NODE_HEIGHT = 88;
const GAP_X = 80;
const GAP_Y = 80;
const EXTERNAL_GAP = 112;

function positionExternalShareholders(nodes) {
  const current = nodes.find((n) => n.id === 'current');
  if (!current) return nodes;

  const externals = nodes.filter((n) => n.type === 'externalShareholder');
  const left = externals.filter((_, i) => i % 2 === 0);
  const right = externals.filter((_, i) => i % 2 === 1);
  const cx = current.position?.x ?? 0;
  const cy = current.position?.y ?? 0;

  left.forEach((n, i) => {
    const stackOffset = i * (EXTERNAL_NODE_HEIGHT + 24) - ((left.length - 1) * (EXTERNAL_NODE_HEIGHT + 24)) / 2;
    n.position = {
      x: cx - NODE_WIDTH - EXTERNAL_GAP - EXTERNAL_NODE_WIDTH,
      y: cy + (NODE_HEIGHT - EXTERNAL_NODE_HEIGHT) / 2 + stackOffset,
    };
  });
  right.forEach((n, i) => {
    const stackOffset = i * (EXTERNAL_NODE_HEIGHT + 24) - ((right.length - 1) * (EXTERNAL_NODE_HEIGHT + 24)) / 2;
    n.position = {
      x: cx + NODE_WIDTH + EXTERNAL_GAP,
      y: cy + (NODE_HEIGHT - EXTERNAL_NODE_HEIGHT) / 2 + stackOffset,
    };
  });

  return nodes;
}

// Custom Company Node Component
const CompanyNode = ({ id, data }) => {
  const { setSelectedNode, onToggle, layoutDirection } = useContext(HierarchyChartContext);
  const [isHovered, setIsHovered] = useState(false);

  const isLR = layoutDirection === 'LR';
  const targetHandlePos = isLR ? Position.Left : Position.Top;
  const sourceHandlePos = isLR ? Position.Right : Position.Bottom;

  const getTypeStyles = (type, isCurrentCompany) => {
    if (isCurrentCompany) {
      // White background with red outline for current company
      return { bg: 'linear-gradient(135deg, #ffffff 0%, #fafafa 100%)', border: '#b91c1c', text: '#1f2937' };
    }
    switch (type) {
      case 'holding':
      case 'Parent':
      case 'Ultimate Parent':
        return { bg: 'linear-gradient(135deg, #7f1d1d 0%, #991b1b 100%)', border: '#b91c1c', text: 'white' };
      case 'Subsidiary':
      case 'subsidiary':
        return { bg: 'linear-gradient(135deg, #b91c1c 0%, #dc2626 100%)', border: '#ef4444', text: 'white' };
      case 'Associate':
      case 'associate':
        return { bg: 'linear-gradient(135deg, #f87171 0%, #fca5a5 100%)', border: '#fecaca', text: '#7f1d1d' };
      case 'joint-venture':
        return { bg: 'linear-gradient(135deg, #dc2626 0%, #ef4444 100%)', border: '#f87171', text: 'white' };
      default:
        return { bg: 'linear-gradient(135deg, #6b7280 0%, #9ca3af 100%)', border: '#d1d5db', text: 'white' };
    }
  };

  const styles = getTypeStyles(data.type, data.isCurrentCompany);

  const handleClick = (e) => {
    if (data.onClick) {
      data.onClick(id, data);
    }
    setSelectedNode({ id, data });
  };

  const handleToggleClick = (e) => {
    e.stopPropagation();
    onToggle(id);
  };

  return (
    <div
      onClick={handleClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        background: styles.bg,
        border: `3px solid ${styles.border}`,
        borderRadius: 12,
        padding: '12px 16px',
        width: NODE_WIDTH,
        color: styles.text,
        boxShadow: data.isCurrentCompany 
          ? '0 8px 20px rgba(185, 28, 28, 0.3)' 
          : '0 6px 16px rgba(0,0,0,0.15)',
        position: 'relative',
        cursor: data.onClick ? 'pointer' : 'default',
        transition: 'transform 0.2s, box-shadow 0.2s',
        transform: isHovered && data.onClick ? 'translateY(-2px)' : 'none',
      }}
    >
      <Handle type="target" position={targetHandlePos} style={{ background: styles.border }} />
      <Handle type="source" position={sourceHandlePos} style={{ background: styles.border }} />
      {data.allowSideConnections && (
        <>
          <Handle type="target" id="left" position={Position.Left} style={{ background: '#1565C0' }} />
          <Handle type="target" id="right" position={Position.Right} style={{ background: '#1565C0' }} />
        </>
      )}

      {/* Type Badge */}
      <div
        style={{
          position: 'absolute',
          top: -10,
          right: -10,
          background: data.isCurrentCompany 
            ? '#b91c1c' 
            : (data.type === 'Parent' || data.type === 'holding' || data.type === 'Ultimate Parent') 
              ? '#fbbf24' 
              : (data.type === 'Associate' || data.type === 'associate')
                ? '#fed7aa'
                : 'white',
          color: data.isCurrentCompany 
            ? 'white' 
            : (data.type === 'Parent' || data.type === 'holding' || data.type === 'Ultimate Parent') 
              ? '#78350f' 
              : '#374151',
          fontSize: 9,
          fontWeight: 600,
          padding: '3px 8px',
          borderRadius: 10,
          textTransform: 'uppercase',
          boxShadow: '0 2px 4px rgba(0,0,0,0.15)',
        }}
      >
        {data.isCurrentCompany 
          ? '★ Current' 
          : (data.type === 'Parent' || data.type === 'holding') 
            ? '★ Parent' 
            : data.type === 'Ultimate Parent'
              ? '★ Ultimate Parent'
              : data.type}
      </div>

      {/* Company Name */}
      <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 4, lineHeight: 1.3 }}>
        {data.label}
      </div>

      {/* Trade License */}
      {data.tradeLicenseNumber && (
        <div style={{ fontSize: 11, opacity: 0.85, marginBottom: 4 }}>
          {data.tradeLicenseNumber}
        </div>
      )}

      {/* Shareholding - only show for non-current companies */}
      {data.shareholding !== undefined && !data.isCurrentCompany && (
        <div style={{ 
          fontSize: 11, 
          background: 'rgba(255,255,255,0.2)', 
          padding: '2px 8px', 
          borderRadius: 8,
          display: 'inline-block',
          marginTop: 4 
        }}>
          {formatShareholdingPercent(data.shareholding, { suffix: false })}% Shareholding
        </div>
      )}

      {/* Status */}
      {data.status && (
        <div style={{
          position: 'absolute',
          bottom: 8,
          right: 8,
          fontSize: 9,
          fontWeight: 600,
          padding: '2px 6px',
          borderRadius: 4,
          background: data.status === 'ACTIVE' ? 'rgba(127, 219, 131, 0.3)' : 'rgba(158, 158, 158, 0.3)',
          color: 'white',
          textTransform: 'uppercase',
        }}>
          {data.status}
        </div>
      )}

      {/* Expand/Collapse Button */}
      {data.hasChildren && (
        <div
          onClick={handleToggleClick}
          className="nodrag"
          style={{
            position: 'absolute',
            top: isLR ? '50%' : 'auto',
            bottom: isLR ? 'auto' : -12,
            right: isLR ? -12 : 'auto',
            left: isLR ? 'auto' : '50%',
            transform: isLR ? 'translateY(-50%)' : 'translateX(-50%)',
            width: 22,
            height: 22,
            borderRadius: '50%',
            background: 'white',
            border: `2px solid ${styles.border}`,
            color: '#334155',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 14,
            fontWeight: 700,
            cursor: 'pointer',
            boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
            zIndex: 5,
          }}
        >
          {data.isExpanded ? '−' : '+'}
        </div>
      )}
    </div>
  );
};

const ExternalShareholderNode = ({ data }) => {
  const isLeftSource = data.side === 'left';
  const theme = getSideShareholderChartTheme(
    data.isGroupCompany ? 'CORPORATE' : (data.subtype || data.shareholderType)
  );

  return (
    <div
      style={{
        background: theme.gradient,
        border: `3px solid ${theme.border}`,
        borderRadius: 10,
        padding: '10px 12px',
        width: EXTERNAL_NODE_WIDTH,
        minHeight: EXTERNAL_NODE_HEIGHT,
        color: 'white',
        boxShadow: `0 6px 16px ${theme.edgeStroke}59`,
        position: 'relative',
      }}
    >
      <Handle
        type="source"
        id={isLeftSource ? 'out-right' : 'out-left'}
        position={isLeftSource ? Position.Right : Position.Left}
        style={{ background: theme.handle }}
      />
      <div
        style={{
          position: 'absolute',
          top: -10,
          right: 8,
          background: theme.selectedBorder,
          color: theme.border,
          fontSize: 8,
          fontWeight: 700,
          padding: '2px 6px',
          borderRadius: 8,
          textTransform: 'uppercase',
        }}
      >
        {theme.badge}
      </div>
      <div style={{ fontWeight: 700, fontSize: 12, lineHeight: 1.35, marginTop: 4 }}>
        {data.label}
      </div>
      <div style={{ fontSize: 10, opacity: 0.9, marginTop: 6 }}>
        {getShareholderTypeLabel(data.subtype || data.shareholderType)}
        {data.stake != null ? ` • ${formatShareholdingPercent(data.stake)}` : ''}
      </div>
    </div>
  );
};

const STEP_OWNERSHIP_LABEL_OFFSET = 28;

function getStepOwnershipLabelPosition({ targetX, targetY, sourcePosition, targetPosition }) {
  const offset = STEP_OWNERSHIP_LABEL_OFFSET;
  if (sourcePosition === Position.Bottom && targetPosition === Position.Top) {
    return { labelX: targetX, labelY: targetY - offset };
  }
  if (sourcePosition === Position.Right && targetPosition === Position.Left) {
    return { labelX: targetX - offset, labelY: targetY };
  }
  if (sourcePosition === Position.Left && targetPosition === Position.Right) {
    return { labelX: targetX + offset, labelY: targetY };
  }
  if (sourcePosition === Position.Top && targetPosition === Position.Bottom) {
    return { labelX: targetX, labelY: targetY + offset };
  }
  return { labelX: targetX, labelY: targetY - offset };
}

function getExternalOwnershipLabelPosition({
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
}) {
  if (
    (sourcePosition === Position.Right && targetPosition === Position.Left)
    || (sourcePosition === Position.Left && targetPosition === Position.Right)
  ) {
    return {
      labelX: (sourceX + targetX) / 2,
      labelY: (sourceY + targetY) / 2,
    };
  }
  return getStepOwnershipLabelPosition({
    targetX,
    targetY,
    sourcePosition,
    targetPosition,
  });
}

// Custom Edge with Ownership Percentage
const OwnershipEdge = ({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  data,
  markerEnd,
  style,
}) => {
  const { edgePathType } = useContext(HierarchyChartContext);
  const isStep = edgePathType === 'step';
  const isExternal = data?.isExternal;
  const sideTheme = isExternal
    ? getSideShareholderChartTheme(data?.isGroupCompany ? 'CORPORATE' : 'EXTERNAL')
    : null;

  let edgePath, labelX, labelY;

  if (isStep) {
    [edgePath, , ,] = getSmoothStepPath({
      sourceX,
      sourceY,
      sourcePosition,
      targetX,
      targetY,
      targetPosition,
      borderRadius: 16,
    });
    ({ labelX, labelY } = isExternal
      ? getExternalOwnershipLabelPosition({
        sourceX,
        sourceY,
        targetX,
        targetY,
        sourcePosition,
        targetPosition,
      })
      : getStepOwnershipLabelPosition({
        targetX,
        targetY,
        sourcePosition,
        targetPosition,
      }));
  } else {
    [edgePath, labelX, labelY] = getBezierPath({
      sourceX,
      sourceY,
      sourcePosition,
      targetX,
      targetY,
      targetPosition,
    });
  }

  const getStakeColor = (stake) => {
    if (stake >= 75) return { bg: '#fee2e2', border: '#b91c1c', text: '#7f1d1d' };
    if (stake >= 50) return { bg: '#fecaca', border: '#dc2626', text: '#991b1b' };
    return { bg: '#fed7aa', border: '#f97316', text: '#c2410c' };
  };

  const colors = getStakeColor(data?.stake || 0);

  return (
    <>
      <BaseEdge path={edgePath} markerEnd={markerEnd} style={style} />
      {data?.stake && (
        <EdgeLabelRenderer>
          <div
            style={{
              position: 'absolute',
              transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
              fontSize: 11,
              fontWeight: 700,
              background: sideTheme ? sideTheme.labelBg : colors.bg,
              color: sideTheme ? sideTheme.labelColor : colors.text,
              border: `2px solid ${sideTheme ? sideTheme.labelBorder : colors.border}`,
              padding: '3px 8px',
              borderRadius: 16,
              pointerEvents: 'all',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              zIndex: 10,
            }}
            className="nodrag nopan"
          >
            {formatShareholdingPercent(data.stake, { suffix: false })}%
          </div>
        </EdgeLabelRenderer>
      )}
    </>
  );
};

// Node and Edge Types
const nodeTypes = { company: CompanyNode, externalShareholder: ExternalShareholderNode };
const edgeTypes = { ownership: OwnershipEdge };

// Layout Engine
const calculateTreeLayout = (nodes, edges, direction) => {
  const isLR = direction === 'LR';
  const nodeMap = new Map(nodes.map(n => [n.id, { ...n, children: [] }]));
  const roots = [];
  const visited = new Set();

  const childrenMap = new Map();
  edges.forEach(edge => {
    if (!nodeMap.has(edge.source) || !nodeMap.has(edge.target)) return;
    if (!childrenMap.has(edge.source)) childrenMap.set(edge.source, []);
    childrenMap.get(edge.source).push(edge.target);
    visited.add(edge.target);
  });

  nodes.forEach(n => {
    if (!visited.has(n.id)) roots.push(n.id);
  });

  const getSize = (nodeId) => {
    const node = nodeMap.get(nodeId);
    if (!node || node.hidden) return { width: 0, height: 0 };

    const childrenIds = childrenMap.get(nodeId) || [];
    const visibleChildren = childrenIds.filter(cid => {
      const child = nodeMap.get(cid);
      return child && !child.hidden;
    });

    if (visibleChildren.length === 0) {
      return { width: NODE_WIDTH, height: NODE_HEIGHT };
    }

    const childSizes = visibleChildren.map(cid => getSize(cid));

    let contentWidth = 0;
    let contentHeight = 0;

    if (isLR) {
      contentWidth = Math.max(...childSizes.map(s => s.width));
      contentHeight = childSizes.reduce((sum, s) => sum + s.height, 0) + (childSizes.length - 1) * GAP_Y;
    } else {
      contentWidth = childSizes.reduce((sum, s) => sum + s.width, 0) + (childSizes.length - 1) * GAP_X;
      contentHeight = Math.max(...childSizes.map(s => s.height));
    }

    let totalWidth, totalHeight;

    if (isLR) {
      totalWidth = NODE_WIDTH + GAP_X + contentWidth;
      totalHeight = Math.max(NODE_HEIGHT, contentHeight);
    } else {
      totalWidth = Math.max(NODE_WIDTH, contentWidth);
      totalHeight = NODE_HEIGHT + GAP_Y + contentHeight;
    }

    node.layoutInfo = {
      childSizes,
      visibleChildren,
      totalWidth,
      totalHeight,
      childrenStartX: isLR ? NODE_WIDTH + GAP_X : (totalWidth - contentWidth) / 2,
      childrenStartY: isLR ? (totalHeight - contentHeight) / 2 : NODE_HEIGHT + GAP_Y,
    };

    return { width: totalWidth, height: totalHeight };
  };

  const setPosition = (nodeId, x, y) => {
    const node = nodeMap.get(nodeId);
    if (!node || node.hidden) return;

    const info = node.layoutInfo;
    if (!info) {
      node.position = { x, y };
      return;
    }

    let parentX, parentY;
    if (isLR) {
      parentX = x;
      parentY = y + (info.totalHeight - NODE_HEIGHT) / 2;
    } else {
      parentX = x + (info.totalWidth - NODE_WIDTH) / 2;
      parentY = y;
    }

    node.position = { x: parentX, y: parentY };

    let currentX = x + info.childrenStartX;
    let currentY = y + info.childrenStartY;

    info.visibleChildren.forEach((childId, idx) => {
      const childSize = info.childSizes[idx];
      if (isLR) {
        setPosition(childId, currentX, currentY);
        currentY += childSize.height + GAP_Y;
      } else {
        setPosition(childId, currentX, currentY);
        currentX += childSize.width + GAP_X;
      }
    });
  };

  let startX = 0;
  roots.forEach(rootId => {
    if (!nodeMap.has(rootId)) return;
    const size = getSize(rootId);
    setPosition(rootId, startX, 0);
    startX += size.width + GAP_X * 2;
  });

  const layoutedNodes = nodes.map(n => {
    const processed = nodeMap.get(n.id);
    return {
      ...n,
      position: processed?.position || { x: 0, y: 0 },
    };
  });

  return { nodes: layoutedNodes, edges };
};

// Inner Flow Component
const HierarchyFlow = ({ 
  nodes: initialNodes, 
  edges: initialEdges, 
  onNodeClick,
  height = 400,
  showControls = true,
}) => {
  const [selectedNode, setSelectedNode] = useState(null);
  const [edgePathType, setEdgePathType] = useState('step');
  const [layoutDirection, setLayoutDirection] = useState('TB');
  const [layoutKey, setLayoutKey] = useState(0); // Force layout recalculation
  
  // Pre-calculate initial layout before setting state
  const getInitialLayoutedNodes = useCallback(() => {
    const visibleNodes = initialNodes.filter(n => !n.hidden);
    const treeNodes = visibleNodes.filter((n) => n.type !== 'externalShareholder');
    const treeNodeIds = new Set(treeNodes.map((n) => n.id));
    const treeEdges = initialEdges.filter(
      (e) => treeNodeIds.has(e.source) && treeNodeIds.has(e.target)
    );
    const { nodes: layoutedNodes } = calculateTreeLayout(treeNodes, treeEdges, 'TB');

    let merged = initialNodes.map(n => {
      const layouted = layoutedNodes.find(ln => ln.id === n.id);
      if (layouted) {
        return { ...n, position: layouted.position };
      }
      return n;
    });
    merged = positionExternalShareholders(merged);
    return merged;
  }, [initialNodes, initialEdges]);

  const [nodes, setNodes] = useNodesState(getInitialLayoutedNodes());
  const [edges, setEdges] = useEdgesState(initialEdges);

  const { fitView } = useReactFlow();

  // Update nodes/edges when props change - recalculate layout
  useEffect(() => {
    const visibleNodes = initialNodes.filter(n => !n.hidden);
    const treeNodes = visibleNodes.filter((n) => n.type !== 'externalShareholder');
    const treeNodeIds = new Set(treeNodes.map((n) => n.id));
    const treeEdges = initialEdges.filter(
      (e) => treeNodeIds.has(e.source) && treeNodeIds.has(e.target)
    );
    const { nodes: layoutedNodes } = calculateTreeLayout(treeNodes, treeEdges, layoutDirection);

    let newNodes = initialNodes.map(n => {
      const layouted = layoutedNodes.find(ln => ln.id === n.id);
      if (layouted) {
        return { ...n, position: layouted.position };
      }
      return n;
    });

    newNodes = positionExternalShareholders(newNodes);

    setNodes(newNodes);
    setEdges(initialEdges);
    setLayoutKey(k => k + 1); // Trigger re-layout
  }, [initialNodes, initialEdges, layoutDirection, setNodes, setEdges]);

  // Fit view after initial render and layout changes
  useEffect(() => {
    const timer = setTimeout(() => {
      fitView({ padding: 0.2, duration: 300 });
    }, 100);
    return () => clearTimeout(timer);
  }, [layoutKey, fitView]);

  const getDescendants = (nodeId, allEdges, descendants = []) => {
    const children = allEdges.filter(edge => edge.source === nodeId).map(edge => edge.target);
    for (const childId of children) {
      descendants.push(childId);
      getDescendants(childId, allEdges, descendants);
    }
    return descendants;
  };

  const getChildren = (nodeId, allEdges) => {
    return allEdges.filter(edge => edge.source === nodeId).map(edge => edge.target);
  };

  const onToggle = useCallback((nodeId) => {
    setNodes((nds) => {
      const node = nds.find(n => n.id === nodeId);
      if (!node) return nds;

      const isCurrentlyExpanded = node.data.isExpanded;
      const childrenIds = getChildren(nodeId, edges);

      if (isCurrentlyExpanded) {
        const allDescendants = getDescendants(nodeId, edges);
        return nds.map((n) => {
          if (n.id === nodeId) {
            return { ...n, data: { ...n.data, isExpanded: false } };
          }
          if (allDescendants.includes(n.id)) {
            return { ...n, hidden: true, data: { ...n.data, isExpanded: false } };
          }
          return n;
        });
      } else {
        return nds.map((n) => {
          if (n.id === nodeId) {
            return { ...n, data: { ...n.data, isExpanded: true } };
          }
          if (childrenIds.includes(n.id)) {
            return { ...n, hidden: false };
          }
          return n;
        });
      }
    });
    setLayoutKey(k => k + 1); // Trigger re-layout after toggle
  }, [edges, setNodes]);

  // Apply layout when direction changes or nodes visibility changes
  useLayoutEffect(() => {
    const visibleNodes = nodes.filter(n => !n.hidden);
    if (visibleNodes.length === 0) return;
    
    const { nodes: layoutedNodes } = calculateTreeLayout(visibleNodes, edges, layoutDirection);

    setNodes((nds) => {
      return nds.map(n => {
        const layouted = layoutedNodes.find(ln => ln.id === n.id);
        if (layouted) {
          return { ...n, position: layouted.position };
        }
        return n;
      });
    });
  }, [layoutDirection, layoutKey, edges, setNodes]);

  const onPaneClick = useCallback(() => {
    setSelectedNode(null);
  }, []);

  return (
    <HierarchyChartContext.Provider 
      value={{ 
        selectedNode, 
        setSelectedNode, 
        onToggle, 
        edgePathType, 
        layoutDirection 
      }}
    >
      <Box sx={{ 
        width: '100%', 
        height, 
        position: 'relative',
        '& .react-flow__attribution': {
          display: 'none',
        },
      }}>
        {/* Controls */}
        {showControls && (
          <Box 
            sx={{ 
              position: 'absolute', 
              top: 10, 
              right: 10, 
              zIndex: 10,
              display: 'flex',
              gap: 1,
              background: 'white',
              padding: '4px 8px',
              borderRadius: 2,
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            }}
          >
            <ToggleButtonGroup
              value={layoutDirection}
              exclusive
              onChange={(e, val) => val && setLayoutDirection(val)}
              size="small"
            >
              <ToggleButton value="TB" title="Vertical Layout">
                <VerticalIcon fontSize="small" />
              </ToggleButton>
              <ToggleButton value="LR" title="Horizontal Layout">
                <HorizontalIcon fontSize="small" />
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        )}

        <ReactFlow
          nodes={nodes}
          edges={edges}
          onPaneClick={onPaneClick}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          fitView
          fitViewOptions={{ padding: 0.2 }}
          minZoom={0.3}
          maxZoom={1.5}
          nodesDraggable={false}
          nodesConnectable={false}
          elementsSelectable={false}
        >
          <Controls position="bottom-left" showInteractive={false} />
          <Background variant="dots" gap={16} size={1} color="#e2e8f0" />
        </ReactFlow>
      </Box>
    </HierarchyChartContext.Provider>
  );
};

// Main Export Component
const CompanyHierarchyChart = ({ 
  nodes, 
  edges, 
  onNodeClick,
  height = 400,
  showControls = true,
}) => {
  return (
    <ReactFlowProvider>
      <HierarchyFlow 
        nodes={nodes} 
        edges={edges} 
        onNodeClick={onNodeClick}
        height={height}
        showControls={showControls}
      />
    </ReactFlowProvider>
  );
};

export default CompanyHierarchyChart;

// Helper function to build hierarchy data from company relationships
export const buildHierarchyData = (
  company,
  subsidiaries = [],
  parentCompanies = [],
  onCompanyClick,
  externalShareholders = [],
) => {
  const nodes = [];
  const edges = [];
  const hasExternals = externalShareholders.length > 0;

  // Add parent companies (if any) - sorted by level (ultimate parent first)
  const sortedParents = [...parentCompanies].sort((a, b) => (b.level || 0) - (a.level || 0));
  
  sortedParents.forEach((parent, index) => {
    const isUltimateParent = index === 0 && sortedParents.length > 1;
    const nextTarget = index < sortedParents.length - 1 
      ? `parent-${sortedParents[index + 1].id}` 
      : 'current';
    
    nodes.push({
      id: `parent-${parent.id}`,
      type: 'company',
      position: { x: 0, y: 0 },
      hidden: false, // Explicitly set to visible
      data: {
        label: parent.name,
        type: parent.type || 'Parent',
        tradeLicenseNumber: parent.tradeLicenseNumber,
        status: parent.status,
        shareholding: parent.shareholdingInCurrent,
        hasChildren: true,
        isExpanded: true,
        onClick: onCompanyClick ? () => onCompanyClick(parent.id) : null,
      },
    });

    // Edge from this parent to next in chain (or to current company)
    edges.push({
      id: `parent-${parent.id}-to-${nextTarget}`,
      source: `parent-${parent.id}`,
      target: nextTarget,
      type: 'ownership',
      data: { stake: parent.shareholdingInCurrent || 100 },
      style: { stroke: '#b91c1c', strokeWidth: 2 },
      markerEnd: { type: MarkerType.ArrowClosed, color: '#b91c1c' },
    });
  });

  // Add current company
  nodes.push({
    id: 'current',
    type: 'company',
    position: { x: 0, y: 0 },
    hidden: false, // Explicitly set to visible
    data: {
      label: company.name,
      type: company.type,
      tradeLicenseNumber: company.tradeLicenseNumber,
      status: company.status,
      isCurrentCompany: true,
      allowSideConnections: hasExternals,
      hasChildren: subsidiaries.length > 0,
      isExpanded: true,
      // Don't show shareholding on current company - it's shown on the edge
    },
  });

  // Add subsidiaries
  subsidiaries.forEach((sub) => {
    nodes.push({
      id: `sub-${sub.id}`,
      type: 'company',
      position: { x: 0, y: 0 },
      hidden: false, // Explicitly set to visible
      data: {
        label: sub.name,
        type: sub.type || 'Subsidiary',
        tradeLicenseNumber: sub.tradeLicenseNumber,
        status: sub.status,
        shareholding: sub.shareholding,
        hasChildren: false,
        isExpanded: true,
        onClick: onCompanyClick ? () => onCompanyClick(sub.id) : null,
      },
    });

    // Edge from current to subsidiary
    edges.push({
      id: `current-to-sub-${sub.id}`,
      source: 'current',
      target: `sub-${sub.id}`,
      type: 'ownership',
      data: { stake: sub.shareholding || 100 },
      style: { stroke: '#dc2626', strokeWidth: 2 },
      markerEnd: { type: MarkerType.ArrowClosed, color: '#dc2626' },
    });
  });

  externalShareholders.forEach((ext, idx) => {
    const nodeId = `external-${ext.id}`;
    const isLeft = idx % 2 === 0;
    const theme = getSideShareholderChartTheme(ext.shareholderType || ext.type);
    nodes.push({
      id: nodeId,
      type: 'externalShareholder',
      position: { x: 0, y: 0 },
      data: {
        label: ext.shareholderName || ext.name,
        subtype: ext.shareholderType || ext.type,
        stake: ext.percentage,
        side: isLeft ? 'left' : 'right',
        isGroupCompany: theme.isGroupCompany,
      },
    });
    edges.push({
      id: `${nodeId}-to-current`,
      source: nodeId,
      target: 'current',
      sourceHandle: isLeft ? 'out-right' : 'out-left',
      targetHandle: isLeft ? 'left' : 'right',
      type: 'ownership',
      data: { stake: ext.percentage, isExternal: true, isGroupCompany: theme.isGroupCompany },
      style: { stroke: theme.edgeStroke, strokeWidth: 2 },
      markerEnd: { type: MarkerType.ArrowClosed, color: theme.edgeMarker },
    });
  });

  return { nodes, edges };
};
